import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers,
  FaBox,
  FaShoppingCart,
  FaBook,
  FaAppleAlt,
  FaChartLine,
  FaUserPlus,
  FaHospital,
  FaUserMd,
  FaPlus,
  FaEdit,
  FaArrowRight,
  FaCalendarAlt,
  FaRupeeSign,
  FaPhoneAlt,
  FaHeartbeat,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';
import { database } from '../firebase';
import { ref as dbRef, onValue } from 'firebase/database';

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalProducts: 0,
    totalOrders: 0,
    totalEbooks: 0,
    totalDietPlans: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [hospitalInfo, setHospitalInfo] = useState(null);

  // Fetch all dashboard data from Firebase
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch users count
        const usersRef = dbRef(database, 'Dr/users');
        onValue(usersRef, (snapshot) => {
          if (snapshot.exists()) {
            const usersData = snapshot.val();
            const usersArray = Object.keys(usersData).map(key => ({
              id: key,
              ...usersData[key]
            }));
            
            setDashboardData(prev => ({ ...prev, totalUsers: usersArray.length }));
            
            // Get recent 2 users (sorted by creation date)
            const sortedUsers = usersArray
              .filter(user => user.createdAt)
              .sort((a, b) => b.createdAt - a.createdAt)
              .slice(0, 2);
            setRecentUsers(sortedUsers);
          }
        });

        // Fetch products count
        const productsRef = dbRef(database, 'Dr/products');
        onValue(productsRef, (snapshot) => {
          if (snapshot.exists()) {
            const productsData = snapshot.val();
            setDashboardData(prev => ({ ...prev, totalProducts: Object.keys(productsData).length }));
          }
        });

        // Fetch orders count and recent orders
        const ordersRef = dbRef(database, 'Dr/orders');
        onValue(ordersRef, (snapshot) => {
          if (snapshot.exists()) {
            const ordersData = snapshot.val();
            const ordersArray = [];
            
            // Iterate through mobile numbers
            Object.keys(ordersData).forEach((mobileNumber) => {
              const userOrders = ordersData[mobileNumber];
              
              // Iterate through order numbers for each mobile
              Object.keys(userOrders).forEach((orderNumber) => {
                const order = userOrders[orderNumber];
                ordersArray.push({
                  ...order,
                  mobileNumber: mobileNumber,
                  orderNumberKey: orderNumber,
                });
              });
            });
            
            setDashboardData(prev => ({ ...prev, totalOrders: ordersArray.length }));
            
            // Get recent 4 orders (sorted by creation date)
            const sortedOrders = ordersArray
              .sort((a, b) => b.createdAt - a.createdAt)
              .slice(0, 4);
            setRecentOrders(sortedOrders);
          }
        });

        // Fetch ebooks count
        const ebooksRef = dbRef(database, 'Dr/eBooks');
        onValue(ebooksRef, (snapshot) => {
          if (snapshot.exists()) {
            const ebooksData = snapshot.val();
            setDashboardData(prev => ({ ...prev, totalEbooks: Object.keys(ebooksData).length }));
          }
        });

        // Fetch diet plans count
        const dietPlansRef = dbRef(database, 'Dr/dietPlans');
        onValue(dietPlansRef, (snapshot) => {
          if (snapshot.exists()) {
            const dietPlansData = snapshot.val();
            setDashboardData(prev => ({ ...prev, totalDietPlans: Object.keys(dietPlansData).length }));
          }
        });

        // Fetch hospital info
        const hospitalRef = dbRef(database, 'Dr/hospital');
        onValue(hospitalRef, (snapshot) => {
          if (snapshot.exists()) {
            setHospitalInfo(snapshot.val());
          }
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const summaryCards = [
    { title: 'Total Users', value: dashboardData.totalUsers, icon: <FaUsers />, color: 'from-blue-500 to-cyan-500', link: '/admin/users' },
    { title: 'Total Products', value: dashboardData.totalProducts, icon: <FaBox />, color: 'from-emerald-500 to-green-500', link: '/admin/products' },
    { title: 'Total Orders', value: dashboardData.totalOrders, icon: <FaShoppingCart />, color: 'from-purple-500 to-pink-500', link: '/admin/orders' },
    { title: 'E-Books', value: dashboardData.totalEbooks, icon: <FaBook />, color: 'from-indigo-500 to-blue-500', link: '/admin/ebooks' },
    { title: 'Diet Plans', value: dashboardData.totalDietPlans, icon: <FaAppleAlt />, color: 'from-teal-500 to-emerald-500', link: '/admin/diet-plans' }
  ];

  const quickActions = [
    { title: 'Add Product', icon: <FaPlus className="text-3xl" />, link: '/admin/products/add', color: 'bg-gradient-to-r from-emerald-500 to-green-500' },
    { title: 'Add E-Book', icon: <FaBook className="text-3xl" />, link: '/admin/ebooks/add', color: 'bg-gradient-to-r from-indigo-500 to-blue-500' },
    { title: 'Add Diet Plan', icon: <FaAppleAlt className="text-3xl" />, link: '/admin/diet-plans/add', color: 'bg-gradient-to-r from-teal-500 to-emerald-500' },
    { title: 'Update Hospital Info', icon: <FaEdit className="text-3xl" />, link: '/admin/hospital/details', color: 'bg-gradient-to-r from-purple-500 to-pink-500' }
  ];

  const getStatusColor = (status) => {
    const map = {
      'Confirmed': 'bg-green-100 text-green-800',
      'Pending Verification': 'bg-yellow-100 text-yellow-800',
      'Dispatched': 'bg-blue-100 text-blue-800',
      'Delivered': 'bg-purple-100 text-purple-800',
      'Cancelled': 'bg-red-100 text-red-800'
    };
    return map[status] || 'bg-gray-100 text-gray-800';
  };

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50 to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-6xl text-teal-500 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600 text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-teal-50 to-emerald-50 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-800">Dashboard Overview</h1>
            <p className="text-gray-600 mt-3 text-lg">
              Welcome back, <span className="font-semibold text-teal-700">Admin</span>! Here's your platform summary as of today.
            </p>
          </div>
          <div className="flex items-center gap-3 text-gray-600">
            <FaCalendarAlt className="text-teal-600" />
            <span className="font-medium">{today}</span>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-10">
        {summaryCards.map((card, i) => (
          <Link
            key={i}
            to={card.link}
            className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100"
          >
            <div className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wider">{card.title}</p>
                  <p className="text-3xl font-bold text-gray-800 mt-3">{card.value}</p>
                </div>
                <div className={`p-4 rounded-2xl bg-gradient-to-br ${card.color} text-white shadow-lg`}>
                  {card.icon}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gradient-to-r from-teal-50 to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-sm font-semibold text-teal-700 flex items-center">
                View Details <FaArrowRight className="ml-2 text-xs" />
              </span>
            </div>
          </Link>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-8 mb-10">
        {/* Recent Orders & Users */}
        <div className="xl:col-span-2 space-y-8">
          {/* Recent Orders */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">Recent Orders</h2>
              <Link to="/admin/orders" className="text-teal-600 hover:text-teal-700 font-medium">View All →</Link>
            </div>
            <div className="overflow-x-auto">
              {recentOrders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FaExclamationTriangle className="text-4xl mx-auto mb-3 text-gray-300" />
                  <p>No orders yet</p>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Order ID</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentOrders.map((order) => (
                      <tr key={order.orderId} className="hover:bg-teal-50/30 transition-colors">
                        <td className="px-6 py-5">
                          <div className="font-semibold text-gray-900">{order.orderId}</div>
                          <div className="text-sm text-gray-500">
                            {new Date(order.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-5 font-medium text-gray-800">{order.userName}</td>
                        <td className="px-6 py-5">
                          <div className="flex items-center font-bold text-gray-900">
                            <FaRupeeSign className="mr-1 text-gray-600" />
                            {order.product?.totalPrice?.toLocaleString('en-IN')}
                          </div>
                        </td>
                        <td className="px-6 py-5">
                          <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${getStatusColor(order.orderStatus)}`}>
                            {order.orderStatus}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Recent Users */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">New Users</h2>
              <Link to="/admin/users" className="text-teal-600 hover:text-teal-700 font-medium">View All →</Link>
            </div>
            <div className="p-6 space-y-4">
              {recentUsers.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FaExclamationTriangle className="text-4xl mx-auto mb-3 text-gray-300" />
                  <p>No users yet</p>
                </div>
              ) : (
                recentUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-full flex items-center justify-center">
                        <FaUserPlus className="text-blue-600 text-xl" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800">{user.name}</h4>
                        <p className="text-sm text-gray-600">{user.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </p>
                      <span className="inline-block mt-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        Active
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Hospital Info */}
        <div className="space-y-8">
          <div className="bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 rounded-2xl shadow-xl text-white overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold flex items-center gap-3">
                  <FaHospital />
                  Hospital Info
                </h2>
                <FaHeartbeat className="text-4xl opacity-50 animate-pulse" />
              </div>

              {hospitalInfo ? (
                <div className="space-y-6">
                  <div className="bg-white/10 backdrop-blur rounded-2xl p-5">
                    <h3 className="text-xl font-bold">{hospitalInfo.name}</h3>
                    <p className="text-teal-200 mt-1">Premier Healthcare Provider</p>
                  </div>

                  <div className="space-y-5">
                    {hospitalInfo.doctors && hospitalInfo.doctors.length > 0 && (
                      <div className="flex gap-4">
                        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FaUserMd className="text-xl" />
                        </div>
                        <div>
                          <p className="text-teal-200 text-sm">Chief Consultant</p>
                          <p className="font-bold text-lg">{hospitalInfo.doctors[0].name}</p>
                          <p className="text-sm opacity-90">{hospitalInfo.doctors[0].qualification}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FaPhoneAlt className="text-xl" />
                      </div>
                      <div>
                        <p className="text-teal-200 text-sm">Emergency Hotline</p>
                        <p className="font-bold text-lg">{hospitalInfo.contactNumber || 'Not Available'}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {hospitalInfo.emergencyAvailable && (
                            <span className="px-3 py-1 bg-green-500/30 rounded-lg text-sm">Available 24/7</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-teal-200">No hospital information available</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t border-white/20">
              <Link to="/admin/hospital/details" className="block text-center py-3 bg-white/20 hover:bg-white/30 backdrop-blur rounded-xl font-semibold transition-all">
                <FaEdit className="inline mr-2" />
                Update Information
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-8">Quick Actions</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action) => (
            <Link
              key={action.title}
              to={action.link}
              className={`${action.color} text-white rounded-2xl p-8 flex flex-col items-center justify-center hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 group`}
            >
              <div className="mb-4 transform group-hover:scale-110 transition-transform">
                {action.icon}
              </div>
              <span className="font-bold text-lg text-center">{action.title}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;